/**
 * Creates a throttled function that invokes the provided function at most once per specified time period.
 * Unlike debounce, throttle ensures the function is called at regular intervals.
 * The function executes immediately on the leading edge (first call) and then throttles subsequent calls.
 *
 * @template T - The function type to throttle
 * @param func - The function to throttle
 * @param wait - The number of milliseconds to throttle invocations to
 * @returns A throttled version of the function with a cancel method
 *
 * @example
 * ```ts
 * const throttledScroll = throttle(() => {
 *   console.log('Scrolled');
 * }, 100);
 *
 * window.addEventListener('scroll', throttledScroll);
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function throttle<T extends (...args: any[]) => any>(
  func: T,
  wait: number,
): ((...args: Parameters<T>) => void) & { cancel: () => void } {
  let timeoutId: NodeJS.Timeout | null = null;
  let lastCallTime: number | null = null;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const throttled = function (this: any, ...args: Parameters<T>) {
    const now = Date.now();

    // If this is the first call or enough time has passed since last execution
    if (lastCallTime === null || now - lastCallTime >= wait) {
      lastCallTime = now;
      func.apply(this, args);

      // Clear any existing timeout
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    }
  } as ((...args: Parameters<T>) => void) & { cancel: () => void };

  throttled.cancel = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
    lastCallTime = null;
  };

  return throttled;
}
